/**
 * React Query hooks for trace dosing calculator.
 *
 * These hooks support two usage patterns:
 * 1. New pattern: Pass tankId explicitly as argument
 * 2. Legacy pattern: Call with no argument, uses TankContext internally
 *
 * TODO (Task 8+): Migrate page components to new pattern and remove legacy support
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBatches,
  getTemplates,
  createBatch,
  updateBatch,
  deleteBatch,
  saveAsTemplate,
  deleteTemplate,
} from '@/api/traceDosing';
import { useTank } from '@/contexts/TankContext';
import { CACHE_TIMES } from '@/config/cacheTimes';
import type { CreateBatchRequest, UpdateBatchRequest, SaveAsTemplateRequest } from '@/types/traceDosing';

const BATCHES_KEY = 'dosing-batches';
const TEMPLATES_KEY = 'dosing-templates';

// Helper to get tankId - uses parameter if provided, falls back to context
function useTankId(tankIdParam?: string): string | undefined {
  const { selectedTank } = useTank();
  return tankIdParam ?? selectedTank?.id;
}

export function useDosingBatches(tankIdParam?: string) {
  const tankId = useTankId(tankIdParam);
  return useQuery({
    queryKey: [BATCHES_KEY, tankId],
    queryFn: () => getBatches(tankId!, false),
    enabled: !!tankId,
    staleTime: CACHE_TIMES.LIVE,
  });
}

export function useDosingTemplates(tankIdParam?: string) {
  const tankId = useTankId(tankIdParam);
  return useQuery({
    queryKey: [TEMPLATES_KEY, tankId],
    queryFn: () => getTemplates(tankId!),
    enabled: !!tankId,
    staleTime: CACHE_TIMES.LIVE,
  });
}

export function useCreateBatch(tankIdParam?: string) {
  const tankId = useTankId(tankIdParam);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batch: CreateBatchRequest) => createBatch(tankId!, batch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BATCHES_KEY, tankId] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, tankId] });
    },
  });
}

export function useUpdateBatch(tankIdParam?: string) {
  const tankId = useTankId(tankIdParam);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, batch }: { batchId: string; batch: UpdateBatchRequest }) =>
      updateBatch(tankId!, batchId, batch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BATCHES_KEY, tankId] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, tankId] });
    },
  });
}

export function useDeleteBatch(tankIdParam?: string) {
  const tankId = useTankId(tankIdParam);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) => deleteBatch(tankId!, batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BATCHES_KEY, tankId] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, tankId] });
    },
  });
}

export function useSaveAsTemplate(tankIdParam?: string) {
  const tankId = useTankId(tankIdParam);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, request }: { batchId: string; request: SaveAsTemplateRequest }) =>
      saveAsTemplate(tankId!, batchId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BATCHES_KEY, tankId] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, tankId] });
    },
  });
}

export function useDeleteTemplate(_tankIdParam?: string) {
  // Note: tankIdParam kept for API consistency but tankId is passed through mutation variables
  // to avoid stale closure issues with React Query cache invalidation
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, tankId }: { batchId: string; tankId: string }) =>
      deleteTemplate(tankId, batchId),
    onSuccess: (_data, variables) => {
      // Use tankId from mutation variables, not a closure-captured value
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, variables.tankId] });
    },
  });
}

// Legacy hook for backwards compatibility during migration
// TODO: Remove after page components are updated (Task 8+)
// Note: This creates a template (batch with is_template=true)
export function useCreateTemplate(tankIdParam?: string) {
  const tankId = useTankId(tankIdParam);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBatchRequest) => createBatch(tankId!, { ...data, is_template: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, tankId] });
    },
  });
}
